import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useState } from 'react';
import { formatDistanceToNow } from 'date-fns';
import { batches } from '../api/index.js';
import { Badge, statusBadge } from '../components/ui/badge.js';
import { Button } from '../components/ui/button.js';
import { Card } from '../components/ui/card.js';
import { Upload, ChevronLeft, ChevronRight, Loader2 } from 'lucide-react';
import { SearchableSelect } from '../components/SearchableSelect.js';

const STATUS_OPTS = [
  { value: '', label: 'All statuses' },
  { value: 'csv_pending_mapping', label: 'Needs mapping' },
  { value: 'pending_review', label: 'Pending review' },
  { value: 'applying', label: 'Applying' },
  { value: 'applied', label: 'Applied' },
  { value: 'failed', label: 'Failed' },
  { value: 'apply_paused_expired_jwt', label: 'Paused (expired JWT)' },
];

const SOURCE_OPTS = [
  { value: '', label: 'All sources' },
  { value: 'onix', label: 'ONIX XML' },
  { value: 'csv', label: 'CSV' },
];

export function BatchList() {
  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState('');
  const [sourceFilter, setSourceFilter] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['batches', page, statusFilter, sourceFilter],
    queryFn: () => batches.list(page, statusFilter || undefined, sourceFilter || undefined),
    refetchInterval: 10_000,
  });

  const totalPages = data?.pages ?? 0;

  return (
    <div className="p-8 max-w-5xl mx-auto">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Import Batches</h1>
          <p className="text-gray-500 text-sm mt-1">{data?.total ?? 0} total batches</p>
        </div>
        <Link to="/upload">
          <Button><Upload size={16} /> New Upload</Button>
        </Link>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-4 flex-wrap">
        <div className="min-w-[11rem] max-w-xs">
          <SearchableSelect
            value={statusFilter}
            onChange={(v) => { setStatusFilter(v); setPage(1); }}
            options={STATUS_OPTS}
            placeholder="All statuses"
            allowClear={false}
          />
        </div>
        <div className="min-w-[10rem] max-w-xs">
          <SearchableSelect
            value={sourceFilter}
            onChange={(v) => { setSourceFilter(v); setPage(1); }}
            options={SOURCE_OPTS}
            placeholder="All sources"
            allowClear={false}
          />
        </div>
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16 text-red-500 gap-2 text-sm">
            Failed to load batches — {error instanceof Error ? error.message : 'Unknown error'}
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-gray-100 text-left">
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">File</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Type</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Status</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Records</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Ready</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Review</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide text-right">Blocked</th>
                <th className="px-6 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Uploaded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-50">
              {data?.batches.map((batch) => (
                <tr key={batch.id} className="hover:bg-gray-50 transition-colors cursor-pointer">
                  <td className="px-6 py-4">
                    <Link to={`/batches/${batch.id}`} className="font-medium text-brand-700 hover:text-brand-900 truncate max-w-xs block">
                      {batch.fileName}
                    </Link>
                    <p className="text-xs text-gray-400 font-mono">{batch.id}</p>
                  </td>
                  <td className="px-6 py-4">
                    <span className="text-xs px-1.5 py-0.5 rounded bg-gray-100 text-gray-500 uppercase font-mono">
                      {(batch as any).source === 'csv' ? 'CSV' : 'ONIX'}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <Badge variant={statusBadge(batch.status)}>{batch.status.replace(/_/g, ' ')}</Badge>
                  </td>
                  <td className="px-6 py-4 text-right text-gray-700">{batch.totalRecords}</td>
                  <td className="px-6 py-4 text-right text-green-700 font-medium">{batch.readyCount}</td>
                  <td className="px-6 py-4 text-right text-yellow-700 font-medium">{batch.reviewCount}</td>
                  <td className="px-6 py-4 text-right text-red-700 font-medium">{batch.blockedCount}</td>
                  <td className="px-6 py-4 text-gray-500 text-xs">
                    {formatDistanceToNow(new Date(batch.createdAt), { addSuffix: true })}
                  </td>
                </tr>
              ))}
              {data?.batches.length === 0 && (
                <tr>
                  <td colSpan={8} className="px-6 py-12 text-center text-gray-400">
                    No batches found. {!statusFilter && !sourceFilter && <Link to="/upload" className="text-brand-600 hover:underline">Upload your first file</Link>}.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        )}

        {totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-100 flex items-center justify-between">
            <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
            <div className="flex gap-2">
              <Button variant="secondary" size="sm" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>
                <ChevronLeft size={14} />
              </Button>
              <Button variant="secondary" size="sm" disabled={page >= totalPages} onClick={() => setPage(p => p + 1)}>
                <ChevronRight size={14} />
              </Button>
            </div>
          </div>
        )}
      </Card>
    </div>
  );
}
